import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  RootState,
  resetChatState,
  updateCurrentUploadedImageSourceId,
  updateSocketLoading,
  updateUploadedImageData,
  updateUserMessage,
  useAppDispatch,
  useSendEventMutation,
  isStreamingAdvisor,
  sendStreamingMessage,
  removeLastAdvisorMessage,
  sentRestApiMessage,
  startNewStreamedMessage,
  appendAgentPhaseToStreamedMessage,
  completeStreamedMessage,
  createChatSessionDispatch,
  hydrateChatSession,
  openChatSession,
  useFetchConversationDetailsMutation,
  useLazyDownloadConversationQuery,
  useGetTagsDataMutation
} from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import ChatUIExpand from './chatuiexpand';
import { CtyButton, CtySpinner, SpeechUtility, checkType, CtyShowToast, ErrorBoundary, DownloadModal, ShareModal, generateConversationTextFile, downloadTextFile, downloadFileFromUrl, getCurrentDateTime, useCheckApplicationResource, buildCommandPayloadFields, validateCommandForm, trackAdvisorChat } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { InactivityModal } from './inactivityModal';
import { startAgentStream, streamAgentChat } from './agentconverse/agentstreamingservice';
import { enableChatAutoScroll, scrollChatToBottom } from './chatAutoScroll';
import ConversationsSidebar, { ConversationsSidebarHandle } from './ConversationsSidebar';
import getConversationUrl from '../../../shared/utils/links';

interface ChatAdvisorProps {
  showControls?: boolean;
  agentStreamUrl?: string;
  /**
   * PAL view — the same flag `getAdvisors` takes to hit the PAL endpoint. When true, the `/`
   * agent list only offers agents whose Deploy tab enabled `metadata.showingPal`.
   */
  palView?: boolean;
  /**
   * For guest users, callback to fetch the latest guest token
   * and update it in state. This will be used when the guest
   * token is expired or about to expire.
   */
  fetchLatestGuestToken?: () => Promise<string | null | undefined>;
  /**
   * When provided, continues an existing conversation using this sender_id
   * instead of calling start-stream to create a brand new session. New turns
   * are appended to the same conversation thread server-side.
   */
  existingSenderId?: string;
  /** Content rendered above the live chat thread (e.g. the prior conversation-log messages). */
  leadingContent?: React.ReactNode;
  /** Hides the advisor intro card and conversation starters (used when continuing an existing conversation). */
  hideIntro?: boolean;
  /** Rendered at the top of the message scroller (e.g. advisor name + sticky tabs). */
  scrollHeader?: React.ReactNode;
  /** Enables authenticated web-app conversation switching (never widget/extension builds). */
  showConversationsSidebar?: boolean;
}

interface NetworkStatus {
  online: boolean;
  downlink?: number;
  effectiveType?: string;
  rtt?: number;
}

// Hoist ErrorBoundary-wrapped component to module scope to preserve identity across renders
const ChatUIExpandWithErrorBoundary = ErrorBoundary(ChatUIExpand);

const normalizeHistoricalMessages = (history: any) => {
  const log = history?.conversation_log ?? history?.data?.conversation_log ?? [];
  return log.map((message: any) => {
    const rawMessage = message.message ?? message.content ?? '';
    const prediction = rawMessage?.predictions?.[0];
    const text = typeof rawMessage === 'string'
      ? rawMessage
      : prediction?.answer ?? rawMessage?.answer ?? '';
    const metaData = message.metaData ?? message.meta ?? prediction?.meta ?? [];
    return {
      ...message,
      isUser: message.isUser ?? message.role === 'user',
      isAdvisor: message.isAdvisor ?? ['bot', 'assistant', 'advisor'].includes(message.role),
      isStreaming: false,
      isHistorical: true, // Settled turn: renders in full, never animates
      isFeedbackAllowed: false,
      metaData,
      message: text,
      intent_and_entities: message.intent_and_entities ?? rawMessage?.intent_and_entities,
      comment_history: message.comment_history ?? [],
      feedback_history: message.feedback_history ?? [],
      messageId: message.messageId ?? message.message_id ?? '',
      rephrased_query: message.rephrased_query ?? '',
      comment: message.comment ?? '',
      explainanswer: message.explainanswer ?? {
        answer: text,
        metaData,
        rephrased_query: '',
        reranked_context: {},
        retrieved_context: {},
      },
      aiResponseReceivedTime: String(message.aiResponseReceivedTime ?? message.timestamp ?? ''),
    };
  });
};

export function Chatadvisor(props: Readonly<ChatAdvisorProps>) {
  const { t } = useTranslation();
  const startStreamRef = useRef(false);
  const loadedInitialConversationRef = useRef<string>('');
  const scrolledSessionRef = useRef<string>('');

  const dispatch = useAppDispatch();
  // The sender_id is the session key: start-stream issues it for a new
  // conversation, the history API returns it for an old one. Empty until then.
  const [sessionId, setSessionId] = useState<string>(props.existingSenderId ?? '');
  const [senderId, setSenderId] = useState<string>('');
  const continueExistingRef = useRef(Boolean(props.existingSenderId));
  const sessionDispatch = createChatSessionDispatch(dispatch, sessionId);
  const [fetchConversationDetails] = useFetchConversationDetailsMutation();
  const [loadingConversationId, setLoadingConversationId] = useState<string>('');
  const [streamConnectionError, setStreamConnectionError] = useState<string | null>(null);
  const [isStreamConnectionEstablished, setIsStreamConnectionEstablished] = useState<boolean>(false);
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    online: navigator.onLine,
    downlink: (navigator as any).connection?.downlink,
    effectiveType: (navigator as any).connection?.effectiveType,
    rtt: (navigator as any).connection?.rtt
  });
  const [sendEvent] = useSendEventMutation();

  const [currentSession, setCurrentSession] = useState<number>(0);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isInviteLinkCopied, setIsInviteLinkCopied] = useState<boolean>(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState<boolean>(false);
  const [isDownloadingText, setIsDownloadingText] = useState<boolean>(false);

  const [downloadConversation] = useLazyDownloadConversationQuery();
  const [getTagsData] = useGetTagsDataMutation();

  const { checkAndProceed: checkShare, isChecking: isCheckingShare } = useCheckApplicationResource('share_url');
  const { checkAndProceed: checkDownload, isChecking: isCheckingDownload } = useCheckApplicationResource('download_pdf');

  //Reducers
  const chatRuntime = useSelector((state: RootState) =>
    state.chatAdvisorSliceReducer.sessions?.[sessionId]
  );
  const chatSessions = useSelector((state: RootState) =>
    state.chatAdvisorSliceReducer.sessions
  );
  const {
    chatMessages = [],
    currentActiveSocketObject = {},
    currentChatMessageUploadedImageInfo = { source_id: '', file_name: '' },
    currentChatMentionedEntities = {},
    socketError = {},
    isStreamingInProgress = false,
    isSocketLoading = false,
  } = chatRuntime ?? {};
  const activeChatAdvisor = useSelector((state: RootState) => state.advisor.activeChatAdvisor);
  const { chat_api } = activeChatAdvisor ?? {};
  const token = useSelector((state: RootState) => state.auth.token);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth.user);
  const { apiToken: guestToken, apiKey: guestApiKey } = useSelector((state: RootState) => state.advisor.guestToken);

  const [converseToken, setConverseToken] = useState<string>('');

  useEffect(() => {
    if (!sessionId) return;
    // No title: an unsaved conversation is labelled "New conversation" until the
    // history API supplies its real title. The advisor name is not a title.
    dispatch(openChatSession({
      sessionId,
      advisorId: activeChatAdvisor?.advisor_id,
    }));
  }, [activeChatAdvisor?.advisor_id, dispatch, sessionId]);

  // Opening a conversation lands at the newest turn, like every chat client.
  // Once per conversation only, so it never yanks the view while the user
  // scrolls back through a reply that is still streaming.
  useEffect(() => {
    if (!sessionId || chatMessages.length === 0) return;
    if (scrolledSessionRef.current === sessionId) return;
    scrolledSessionRef.current = sessionId;
    enableChatAutoScroll();
    const frameId = requestAnimationFrame(() => scrollChatToBottom('auto', true));
    return () => cancelAnimationFrame(frameId);
  }, [sessionId, chatMessages.length]);

  const openSession = useCallback((nextSenderId: string) => {
    continueExistingRef.current = true;
    startStreamRef.current = true;
    dispatch(openChatSession({ sessionId: nextSenderId }));
    setSessionId(nextSenderId);
    setSenderId(nextSenderId);
    setStreamConnectionError(null);
    setIsStreamConnectionEstablished(true);
  }, [dispatch]);

  /**
   * Opens a conversation from the sidebar. Sessions are keyed by sender_id; the
   * record id is only needed to fetch a transcript we have not loaded yet.
   */
  const selectConversation = useCallback(async (conversationId: string, senderIdHint?: string) => {
    const cached = senderIdHint ? chatSessions?.[senderIdHint] : undefined;
    if (cached?.chatMessages?.length || cached?.isSocketLoading || cached?.isStreamingInProgress) {
      openSession(senderIdHint as string);
      return;
    }

    setLoadingConversationId(conversationId);
    try {
      const history: any = await fetchConversationDetails({ id: conversationId }).unwrap();
      const resolvedSenderId = history?.sender_id ?? history?.data?.sender_id ?? senderIdHint;
      if (!resolvedSenderId) throw new Error('Conversation details did not include sender_id');

      dispatch(hydrateChatSession({
        sessionId: resolvedSenderId,
        senderId: resolvedSenderId,
        advisorId: activeChatAdvisor?.advisor_id,
        messages: normalizeHistoricalMessages(history),
      }));
      openSession(resolvedSenderId);
    } catch (error) {
      CtyShowToast(checkType.error, t('Unable to load conversation'));
      console.error('Failed to load conversation details:', error);
    } finally {
      setLoadingConversationId('');
    }
  }, [activeChatAdvisor?.advisor_id, chatSessions, dispatch, fetchConversationDetails, openSession, t]);

  // Callers that already know the sender_id (the conversation page, a shared
  // link) continue that thread directly; there is no record id to resolve.
  useEffect(() => {
    if (
      props.existingSenderId &&
      loadedInitialConversationRef.current !== props.existingSenderId
    ) {
      loadedInitialConversationRef.current = props.existingSenderId;
      openSession(props.existingSenderId);
    }
  }, [props.existingSenderId, openSession]);
  // Queue messages clicked before start-stream completes
  const pendingQueueRef = useRef<Array<{
    text: string;
    sources?: { source_id: string; file_name: string }[];
    mentions?: any;
  }>>([]);
  const isFlushingQueueRef = useRef<boolean>(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      setConverseToken(token);
    } else if (!isAuthenticated && guestToken) {
      setConverseToken(guestToken);
    }
  }, [isAuthenticated, token, guestToken])

  // Check if current advisor is streaming
  const isStreaming = isStreamingAdvisor(activeChatAdvisor);

  // Check if we're communicating with an agent
  const isUsingAgent = currentChatMentionedEntities &&
    'aiworker_id' in currentChatMentionedEntities &&
    !('advisor_id' in currentChatMentionedEntities);

  // Agent state
  const [agentSessionId, setAgentSessionId] = useState<string>('');
  const agentStreamStartedRef = useRef(false);
  const agentActiveIndexRef = useRef<number | null>(null);
  const agentSendingRef = useRef(false);
  const commandFormDataRef = useRef<{ formData: Record<string, string>; customMessage: string } | null>(null);

  useEffect(() => {
    const updateNetworkStatus = () => {
      setNetworkStatus({
        online: navigator.onLine,
        downlink: (navigator as any).connection?.downlink,
        effectiveType: (navigator as any).connection?.effectiveType,
        rtt: (navigator as any).connection?.rtt
      });
    };

    // Update when online/offline status changes
    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    // Update when connection type/quality changes
    const connection = (navigator as any).connection;
    if (connection) {
      connection.addEventListener('change', updateNetworkStatus);
    }

    // Initial check
    updateNetworkStatus();

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
      if (connection) {
        connection.removeEventListener('change', updateNetworkStatus);
      }
    };
  }, []);

  // Keep the chat session alive when its view unmounts.
  useEffect(() => {
    return () => {
      setAgentSessionId('');
      agentStreamStartedRef.current = false;
      agentActiveIndexRef.current = null;
      agentSendingRef.current = false;
    };
  }, []);

  useEffect(() => {
    SpeechUtility.setLanguage('en-US');
  }, []);

  // Clear any pending queue when starting a brand new session
  useEffect(() => {
    pendingQueueRef.current = [];
  }, [currentSession]);

  // Flush queued messages in order once a valid sessionId is available
  const flushQueuedMessages = async (activeSessionId?: string) => {
    const session = activeSessionId || senderId;
    if (!session) return;
    if (isFlushingQueueRef.current) return;
    if (!pendingQueueRef.current.length) return;

    try {
      isFlushingQueueRef.current = true;

      // Send queued messages sequentially to preserve order
      // Capture values that can change
      const advisorId = activeChatAdvisor?.advisor_id ?? '';
      const apiUrl = chat_api + 'api/v1/chat';
      const isStream = isStreaming;
      const usingGuest = !isAuthenticated ? guestApiKey : undefined;

      while (pendingQueueRef.current.length > 0) {
        const item = pendingQueueRef.current.shift()!;
        let transText = item.text;

        // Translate only at send time
        if (SpeechUtility.getLanguage() !== 'en-US') {
          const speechUtilityInstance = await SpeechUtility.getInstance(dispatch);
          transText = await speechUtilityInstance.translateText(item.text, 'en-US');
        }

        const sources = item.sources ?? [];
        if (isStream) {
          await sendStreamingMessage(
            apiUrl,
            transText,
            session,
            advisorId,
            sources,
            { ...(item.mentions || {}) },
            converseToken,
            dispatch,
            usingGuest,
            false,
            isAuthenticated,
            !isAuthenticated ? props.fetchLatestGuestToken : undefined,
            session,
          );
        } else {
          await sentRestApiMessage(
            apiUrl,
            transText,
            session,
            advisorId,
            sources,
            { ...(item.mentions || {}) },
            converseToken,
            dispatch,
            usingGuest,
            false,
            activeChatAdvisor?.is_stream,
            isAuthenticated,
            !isAuthenticated ? props.fetchLatestGuestToken : undefined,
            session,
          );
        }
      }
    } catch (e) {
      // If flushing fails, leave remaining items in queue; user can retry
      console.error('Failed to flush queued messages:', e);
    } finally {
      isFlushingQueueRef.current = false;
    }
  };

  const handleStartStream = async (): Promise<boolean> => {
    // Continue-conversation mode: reuse the existing conversation session instead
    // of creating a new one via start-stream. Messages are sent directly to
    // api/v1/chat with this sender_id so they append to the same thread.
    if (continueExistingRef.current) {
      if (!senderId) return false;
      setStreamConnectionError(null);
      setIsStreamConnectionEstablished(true);
      return true;
    }

    try {
      setStreamConnectionError(null);
      setIsStreamConnectionEstablished(false);


      const requestBody = {
        token: converseToken,
        advisor_id: activeChatAdvisor?.advisor_id,
        ...(!isAuthenticated && { 'x-api-key': guestApiKey }),
      };

      const newSenderId = await trackAdvisorChat({
        operation: 'start_stream',
        startAttributes: {
          advisor_id: activeChatAdvisor?.advisor_id ?? null,
        },
        run: async () => {
          const response = await fetch(chat_api + 'api/v1/start-stream', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json , text/event-stream',
              'Authorization': `Bearer ${converseToken}`
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to establish connection: ${response.status} - ${errorText}`);
          }

          const reader = response?.body?.getReader();
          if (!reader) {
            throw new Error('ReadableStream not supported');
          }

          const decoder = new TextDecoder();
          let done = false;
          let data = '';

          while (!done) {
            const { value, done: doneReading } = await reader.read();
            done = doneReading;
            data += decoder.decode(value, { stream: true });
          }

          const parsedData = JSON.parse(data);
          const senderFromStream = parsedData?.sender_id;
          if (!senderFromStream) {
            throw new Error('No sender_id received from start-stream API');
          }
          return senderFromStream as string;
        },
        completeAttributes: (senderIdFromApi) => ({
          sender_id: senderIdFromApi,
          data_yielded: true,
        }),
      });

      dispatch(openChatSession({
        sessionId: newSenderId,
        advisorId: activeChatAdvisor?.advisor_id,
      }));
      setSessionId(newSenderId);
      setSenderId(newSenderId);
      setIsStreamConnectionEstablished(true);
      void flushQueuedMessages(newSenderId);
      return true;
    } catch (error) {
      // Log the error for debugging while providing user-friendly message
      console.error('Stream connection failed:', error);
      setStreamConnectionError('Failed to establish connection, please try again later.');
      setIsStreamConnectionEstablished(false);
      // Allow user to retry interactions
      sessionDispatch(updateSocketLoading(false));
      return false;
    }
  };

  // Initialize stream connection for streaming advisors
  useEffect(() => {
    if (converseToken && !startStreamRef.current) {
      startStreamRef.current = true;
      handleStartStream()

    }
  }, [converseToken]);

  const chatMessagesRef = useRef(chatMessages);

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);

  // Handle agent messages inline
  const handleAgentMessage = async (userInput: string) => {
    try {
      if (agentSendingRef.current) return;
      agentSendingRef.current = true;
      sessionDispatch(updateSocketLoading(true));

      const agent = currentChatMentionedEntities;

      if (agent?.aiworker_id === 'agent_log') {
        const tagsResponse = await getTagsData({
          key: 'claimNumber',
          value: userInput.trim(),
        }).unwrap();

        const agentResponse = {
          isAdvisor: true,
          isUser: false,
          message: '',
          metaData: [],
          isFeedbackAllowed: false,
          feedback: '',
          comment: '',
          rephrased_query: '',
          explainanswer: {
            answer: '',
            metaData: {},
            rephrased_query: '',
            reranked_context: {},
            retrieved_context: {},
          },
          aiResponseReceivedTime: Date.now().toString(),
          agentName: agent?.name,
          claimData: tagsResponse,
          isClaimData: true,
        };

        sessionDispatch(updateUserMessage(agentResponse));
        sessionDispatch(updateSocketLoading(false));
      } else {
        // Existing logic for non-agent_log agents
        // Initialize agent session if needed
        let currentSessionId = agentSessionId;
        if (!currentSessionId) {
          const { session_id } = await startAgentStream({
            baseUrl: import.meta.env.CTY_AGENTS_STREAM_URL || props.agentStreamUrl,
            aiworker_id: agent?.aiworker_id,
            token: converseToken || '',
          });
          if (session_id) {
            setAgentSessionId(session_id);
            currentSessionId = session_id;
          } else {
            throw new Error('Failed to initialize agent session');
          }
        }

        // Create streaming message for agent response
        sessionDispatch(startNewStreamedMessage({
          isAdvisor: true,
          isUser: false,
          message: '',
          agentName: agent?.name,
          aiResponseReceivedTime: Date.now().toString(),
        }));

        const agentCmdJson = agent?.commandJson;
        let payloadFields: Record<string, any> | undefined;
        if (agentCmdJson && commandFormDataRef.current) {
          const { formData, customMessage } = commandFormDataRef.current;
          const result = validateCommandForm(agentCmdJson, formData, customMessage);
          if (!result.valid) {
            sessionDispatch(updateSocketLoading(false));
            agentSendingRef.current = false;
            return;
          }
          payloadFields = buildCommandPayloadFields(agentCmdJson, formData, customMessage, userInput);
        }

        await streamAgentChat({
          baseUrl: import.meta.env.CTY_AGENTS_STREAM_URL || props.agentStreamUrl,
          aiworker_id: agent?.aiworker_id,
          session_id: currentSessionId,
          user_input: payloadFields ? '' : userInput,
          token: converseToken || '',
          ...(payloadFields ? { payloadFields } : {}),
          onChunk: (chunk) => {
            if (chunk?.event) {
              const event = chunk.event;
              const data = chunk.data;

              if (event === 'node_started' && data?.title) {
                // Append executing phase
                sessionDispatch(appendAgentPhaseToStreamedMessage({
                  type: 'executing',
                  message: `${data.title}`,
                  node_id: data.node_id
                }));
              }

              if (event === 'node_finished' && data?.title && data?.node_id) {
                // Append completed phase
                sessionDispatch(appendAgentPhaseToStreamedMessage({
                  type: 'completed',
                  message: `${data.title}`,
                  node_id: data.node_id,
                  ...(data.duration_sec != null && { duration_sec: Number(data.duration_sec) })
                }));
              }

              if (event === 'workflow_finished') {
                // Finalize the response
                const finalText = data?.outputs?.text || data?.text || '';
                const messageId = data?.outputs?.message_id;

                sessionDispatch(completeStreamedMessage({
                  message: finalText || '[no response from Agent]',
                  total_time_taken_seconds: data?.elapsed_time,
                  message_id: messageId,
                  metaData: [
                    {},
                    {},
                    {},
                    {},
                    {},
                    {},
                    { message_id: messageId },
                    { total_time_taken_seconds: data?.elapsed_time }
                  ]
                }));
                sessionDispatch(updateSocketLoading(false));
              }
            }
          },
          onError: (e) => {
            sessionDispatch(updateSocketLoading(false));
            CtyShowToast(checkType.error, e.message || 'Agent communication failed');
          }
        });
      }

    } catch (error) {
      sessionDispatch(updateSocketLoading(false));
      CtyShowToast(checkType.error, (error as Error)?.message ?? 'Agent communication failed');
    } finally {
      agentSendingRef.current = false;
    }
  };

  const buildUserMessage = (
    message: string,
    userInputStartTime: string,
    sources: { source_id: string; file_name: string }[],
    attachedImageFiles?: File[]
  ) => ({
    isUser: true,
    isAdvisor: false,
    message,
    isFeedbackAllowed: false,
    userInputStartTime,
    ...(sources.length > 0 && {
      attachedImageMeta: {
        imagesourceId: sources.map((s) => s.source_id),
        imagetype: sources.map((s) => (s.file_name.split('.').pop() || '').toLowerCase()),
      },
    }),
    ...(attachedImageFiles?.length && { attachedImageFiles }),
  });

  const handleSendEvent = async (userInput: string, imageMeta?: { sources: { source_id: string; file_name: string }[] }, attachedImageFiles?: File[]) => {
    const sources = imageMeta?.sources ?? [];
    if (isUsingAgent) {
      const agentCmdJson = currentChatMentionedEntities?.commandJson;
      const formRef = commandFormDataRef.current;
      const commandFormReady = agentCmdJson && formRef;
      if (commandFormReady) {
        const result = validateCommandForm(agentCmdJson, formRef.formData, formRef.customMessage);
        if (!result.valid) return;
      }
      const userMsg = buildUserMessage(userInput, Date.now().toString(), sources, attachedImageFiles);
      if (commandFormReady) {
        Object.assign(userMsg, {
          commandFormData: { ...formRef.formData },
          commandCustomMessage: formRef.customMessage ?? '',
          commandJson: agentCmdJson,
          aiworkerId: currentChatMentionedEntities?.aiworker_id,
        });
      }
      sessionDispatch(updateUserMessage(userMsg));
      await handleAgentMessage(userInput);
      return;
    }

    if (isSocketLoading || pendingQueueRef.current.length > 0) return;
    if (sources.length > 0) {
      sessionDispatch(updateCurrentUploadedImageSourceId({ source_id: sources[0].source_id, file_name: sources[0].file_name }));
    }
    const apiKeyForGuest = isAuthenticated ? undefined : guestApiKey;
    const fetchToken = isAuthenticated ? undefined : props.fetchLatestGuestToken;

    // If start-stream hasn't completed yet, enqueue and render immediately
    if (!isStreamConnectionEstablished) {
      const userInputStartTime = Date.now().toString();
      sessionDispatch(updateUserMessage(buildUserMessage(userInput, userInputStartTime, sources, attachedImageFiles)));
      sessionDispatch(updateSocketLoading(true));
      pendingQueueRef.current.push({
        text: userInput,
        sources,
        mentions: { ...currentChatMentionedEntities },
      });
      if (sources.length === 0) sessionDispatch(updateCurrentUploadedImageSourceId({ source_id: '', file_name: '' }));
      sessionDispatch(updateUploadedImageData(null));
      if (!startStreamRef.current && converseToken) {
        startStreamRef.current = true;
        void handleStartStream();
      }
      return;
    }

    let transFormedText = userInput;
    const userInputStartTime = Date.now().toString();
    if (SpeechUtility.getLanguage() !== "en-US") {
      const speechUtilityInstance = await SpeechUtility.getInstance(dispatch);
      transFormedText = await speechUtilityInstance.translateText(userInput, "en-US");
    }
    sessionDispatch(updateUserMessage(buildUserMessage(userInput, userInputStartTime, sources, attachedImageFiles)));

    // Check if this advisor supports streaming
    if (isStreaming) {
      try {
        await sendStreamingMessage(
          chat_api + 'api/v1/chat',
          transFormedText,
          senderId ?? '',
          activeChatAdvisor?.advisor_id ?? '',
          sources,
          { ...currentChatMentionedEntities },
          converseToken,
          dispatch,
          apiKeyForGuest,
          false,
          isAuthenticated,
          fetchToken,
          senderId,
        );
      } catch (error) {
        console.error('Streaming message failed, falling back to websocket:', error);
        sessionDispatch(updateSocketLoading(true));
        const imageSourceIds = sources.map((s) => s.source_id);
        const imageTypes = sources.map((s) => (s.file_name.split('.').pop() || '').toLowerCase());
        sendEvent({
          url: chat_api,
          sessionId,
          event: {
            name: 'user_uttered',
            data: {
              message: transFormedText,
              session_id: currentActiveSocketObject?.id,
              imagesourceid: imageSourceIds,
              imagetype: imageTypes,
              mentions: { ...currentChatMentionedEntities },
            },
          },
          advisor: activeChatAdvisor,
        });
      }
    } else {
      sessionDispatch(updateSocketLoading(true));
      await sentRestApiMessage(
        chat_api + 'api/v1/chat',
        transFormedText,
        senderId ?? '',
        activeChatAdvisor?.advisor_id ?? '',
        sources,
        { ...currentChatMentionedEntities },
        converseToken,
        dispatch,
        apiKeyForGuest,
        false,
        activeChatAdvisor?.is_stream,
        isAuthenticated,
        fetchToken,
        senderId,
      );
    }

    // Clean up image data when no images were sent (keep last image for regenerate)
    if (sources.length === 0) sessionDispatch(updateCurrentUploadedImageSourceId({ source_id: '', file_name: '' }));
    sessionDispatch(updateUploadedImageData(null));
  };

  // If connection flips to established and we have queued items, flush them
  useEffect(() => {
    if (isStreamConnectionEstablished && pendingQueueRef.current.length > 0) {
      void flushQueuedMessages();
    }
  }, [isStreamConnectionEstablished]);

  // Add deep think regenerate handler
  const handleDeepThinkRegenerate = async () => {
    // Find the last user message
    const lastUserMessage = [...chatMessages].reverse().find(msg => msg.isUser)?.message;
    if (!lastUserMessage) return;
    // Remove the last advisor message
    sessionDispatch(removeLastAdvisorMessage(undefined));
    const sources = currentChatMessageUploadedImageInfo?.source_id
      ? [{ source_id: currentChatMessageUploadedImageInfo.source_id, file_name: currentChatMessageUploadedImageInfo.file_name || '' }]
      : [];
    await sendStreamingMessage(
      chat_api + 'api/v1/chat',
      lastUserMessage,
      senderId ?? '',
      activeChatAdvisor?.advisor_id ?? '',
      sources,
      { ...currentChatMentionedEntities },
      converseToken,
      dispatch,
      !isAuthenticated ? guestApiKey : undefined,
      true, // deepThinkMode
      isAuthenticated,
      !isAuthenticated ? props.fetchLatestGuestToken : undefined,
      senderId,
    );
  };

  // Check if buttons should be disabled (same logic as Tips & Tricks)
  const isInputDisabled = isStreamingInProgress || isSocketLoading;
  const hasMessages = chatMessages.length > 0;
  const buttonsDisabled = isInputDisabled || !hasMessages;

  // Download handlers
  const handleDownloadPDF = async () => {
    if (!activeChatAdvisor?.advisor_id || !senderId) {
      CtyShowToast(checkType.error, t('Unable to download: Missing advisor or session information'));
      return;
    }

    try {
      setIsDownloadingPDF(true);
      const response = await downloadConversation({
        sender_id: senderId
      }).unwrap();

      const presignedUrl = response?.presigned_url;
      if (!presignedUrl) {
        throw new Error('No presigned URL returned');
      }

      const filename = `conversation-${activeChatAdvisor.name}-${getCurrentDateTime(new Date())}.pdf`;
      await downloadFileFromUrl(presignedUrl, filename);
      setIsDownloadModalOpen(false);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      CtyShowToast(checkType.error, t('Failed to download PDF'));
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleDownloadTextFile = () => {
    try {
      setIsDownloadingText(true);
      const timestamp = getCurrentDateTime(new Date());
      const advisorName = activeChatAdvisor?.name ?? 'Advisor';
      const textContent = generateConversationTextFile(
        chatMessages.map((msg: any) => ({
          message: msg.message,
          isUser: msg.isUser,
          isAdvisor: msg.isAdvisor,
          role: msg.isUser ? 'user' : 'bot'
        })),
        advisorName,
        timestamp
      );
      const filename = `conversation-${advisorName}-${getCurrentDateTime(new Date())}.txt`;
      downloadTextFile(textContent, filename);
      setIsDownloadModalOpen(false);
    } catch (error) {
      console.error('Failed to download text file:', error);
      CtyShowToast(checkType.error, t('Failed to download text file'));
    } finally {
      setIsDownloadingText(false);
    }
  };

  const handleInviteCopyLink = async () => {
    try {
      const shareUrl = getConversationUrl(senderId, activeChatAdvisor?.advisor_id);
      await navigator.clipboard.writeText(shareUrl);
      setIsInviteLinkCopied(true);
      setTimeout(() => {
        setIsInviteLinkCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  // Construct conversation-style share link for display in the ShareModal
  const constructedShareLink = getConversationUrl(senderId, activeChatAdvisor?.advisor_id);

  const handleNewConversation = () => {
    // No key yet: the session is registered once start-stream returns its sender_id.
    continueExistingRef.current = false;
    setSessionId('');
    setSenderId('');
    setStreamConnectionError(null);
    setIsStreamConnectionEstablished(false);
    setCurrentSession((value) => value + 1);
    void handleStartStream();
  };

  const conversationToolbar = (props.showConversationsSidebar || props.showControls) ? (
      <div className="flex w-full items-center justify-between gap-2 p-3 pr-5 pb-0">
        {(props.showConversationsSidebar || props.showControls) && (
        <CtyButton
          id="newsession-btn"
          className="rounded-md p-5 border-1 "
          radius={'lg'}
          startContent={<Icon icon="fa6-solid:plus" width="12" height="22" style={{ color: 'primary' }} />}
          onPress={handleNewConversation}
          variant="bordered"
          size="sm"
        >
          {t('New Conversation')}
        </CtyButton>
        )}
        {props.showControls && <div className="ml-auto flex gap-2">
          <CtyButton
            id="share-btn"
            className="border-2 border-default-300"
            size="sm"
            variant="light"
            onPress={() => checkShare(() => setIsShareModalOpen(true))}
            startContent={isCheckingShare ? <CtySpinner size="sm" /> : <Icon icon="icon-park:share" width="1.2em" height="1.2em" className="dark:invert" />}
            isDisabled={buttonsDisabled || isCheckingShare}
          >
            {t('Share')}
          </CtyButton>
          <CtyButton
            id="download-btn"
            className="border-2 border-default-300"
            size="sm"
            variant="light"
            onPress={() => checkDownload(() => setIsDownloadModalOpen(true))}
            startContent={isCheckingDownload ? <CtySpinner size="sm" /> : <Icon icon="material-symbols:download" width="1.2em" height="1.2em" />}
            isDisabled={buttonsDisabled || isCheckingDownload}
          >
            {t('Download')}
          </CtyButton>
        </div>}
      </div>
  ) : null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <InactivityModal newSessionTrigger={handleNewConversation} />
      {!networkStatus.online && (
        <div className="bg-danger/10 border-l-4 border-danger text-danger p-4 mb-2" role="alert">
          <div className="flex items-center">
            <Icon icon="mdi:wifi-off" className="mr-2" width="20" height="20" />
            <p className="font-medium">You are currently offline. Please check your internet connection.</p>
          </div>
        </div>
      )}
      {networkStatus.online && networkStatus.effectiveType && (networkStatus.effectiveType === 'slow-2g' || networkStatus.effectiveType === '2g' || networkStatus.effectiveType === '3g' || (networkStatus.downlink && networkStatus.downlink < 0.5)) && (
        <div className="bg-warning/10 border-l-4 border-warning text-warning p-4 mb-2" role="alert">
          <div className="flex items-center">
            <Icon icon="mdi:alert-circle" className="mr-2" width="20" height="20" />
            <p className="font-medium">Your internet connection is slow ({networkStatus.effectiveType}). This may affect chat performance.</p>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-hidden relative">
        {props.showConversationsSidebar && <ConversationsSidebarHandle />}
        {loadingConversationId && (
          // A scrim over the thread rather than a blank panel: the chat stays
          // legible underneath and the composer keeps its place instead of
          // leaving a hole. The panel sits above this and stays clickable.
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-[2px]">
            <CtySpinner size="lg" />
            <span className="text-sm text-default-500">{t('Loading conversation...')}</span>
          </div>
        )}
        {
          (socketError?.message ?? streamConnectionError) ?
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-opacity-75">
              <div className="mb-4 text-danger">
                {streamConnectionError ??
                  (import.meta.env.CTY_MODE !== 'production' ?
                    socketError?.message + " please retry..." :
                    "There seems to be an issue connecting to Advisor, please retry...")}
              </div>
              <CtyButton
                className="px-4 py-2 bg-primary text-white rounded"
                onPress={handleNewConversation}
              >
                Reload
              </CtyButton>
            </div>
            :
            <ChatUIExpandWithErrorBoundary
              messages={chatMessages}
              handleSendClick={handleSendEvent}
              currentActiveSocketObject={currentActiveSocketObject}
              converseSenderId={senderId}
              key={currentSession}
              showDisclaimer={props?.showControls || false}
              palView={props.palView}
              isStreamingInProgress={isStreamingInProgress}
              onDeepThinkRegenerate={handleDeepThinkRegenerate}
              commandFormDataRef={commandFormDataRef}
              leadingContent={props.leadingContent}
              hideIntro={props.hideIntro}
              scrollHeader={props.scrollHeader}
              conversationActions={conversationToolbar}
              conversationsSidebar={props.showConversationsSidebar ? (
                <ConversationsSidebar
                  activeSessionId={sessionId}
                  advisorId={activeChatAdvisor?.advisor_id}
                  onSelect={(conversationId, sidebarSenderId) =>
                    void selectConversation(conversationId, sidebarSenderId)
                  }
                />
              ) : undefined}
            />
        }
      </div>

      {/* Download Modal */}
      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onDownloadPDF={handleDownloadPDF}
        onDownloadText={handleDownloadTextFile}
        isDownloadingPDF={isDownloadingPDF}
        isDownloadingText={isDownloadingText}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => {
          setIsShareModalOpen(false);
          setIsInviteLinkCopied(false);
        }}
        onCopyLink={handleInviteCopyLink}
        isInviteLinkCopied={isInviteLinkCopied}
        shareLink={constructedShareLink}
        senderId={senderId}
        advisorId={activeChatAdvisor?.advisor_id ?? ''}
      />
    </div>
  );
}

export default Chatadvisor;

export function handleNewSession(
  dispatch: any,
  setCurrentSession: React.Dispatch<React.SetStateAction<number>>,
  handleStartStream?: () => Promise<boolean>,
  setStreamConnectionError?: React.Dispatch<React.SetStateAction<string | null>>,
  setSenderId?: React.Dispatch<React.SetStateAction<string>>,
  setIsStreamConnectionEstablished?: React.Dispatch<React.SetStateAction<boolean>>
) {
  SpeechUtility.setLanguage('en-US');
  dispatch(resetChatState(undefined));
  setCurrentSession((currentSession) => currentSession + 1);

  // Reset stream states
  if (setSenderId) setSenderId('');
  if (setIsStreamConnectionEstablished) setIsStreamConnectionEstablished(false);
  if (setStreamConnectionError) setStreamConnectionError(null);

  // Establish new stream connection
  if (handleStartStream) {
    handleStartStream().catch((error) => {
      // Log the error and set user-friendly error message
      console.error('Failed to establish new stream connection:', error);
      if (setStreamConnectionError) {
        setStreamConnectionError('Failed to establish connection, please try again later.');
      }
    });
  }


}
